import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Download, Mail, Search, Users, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

export default function AdminNewsletterPage() {
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);
  const [toDelete, setToDelete] = useState(null); // string (single) | "__bulk__" | null
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const queryClient = useQueryClient();

  const { data: subs = [], isLoading } = useQuery({
    queryKey: ["admin-newsletter"],
    queryFn: async () => (await api.get("/admin/newsletter")).data,
  });

  const filtered = useMemo(() => {
    if (!q.trim()) return subs;
    const needle = q.trim().toLowerCase();
    return subs.filter((s) => (s.email || "").toLowerCase().includes(needle));
  }, [subs, q]);

  const isBulk = toDelete === "__bulk__";
  const bulkEmails = useMemo(
    () => filtered.filter((s) => selected.has(s.email)).map((s) => s.email),
    [filtered, selected]
  );
  const allSelected = filtered.length > 0 && filtered.every((s) => selected.has(s.email));

  const toggleOne = (email) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });

  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) filtered.forEach((s) => next.delete(s.email));
      else filtered.forEach((s) => next.add(s.email));
      return next;
    });

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      if (isBulk) {
        const emails = bulkEmails;
        const res = await api.post("/admin/newsletter/bulk-delete", { emails });
        toast.success(`${res.data?.deleted_count ?? emails.length} email(s) supprimé(s)`);
        setSelected(new Set());
      } else {
        await api.delete("/admin/newsletter", { params: { email: toDelete } });
        toast.success(`${toDelete} supprimé`);
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(toDelete);
          return next;
        });
      }
      setToDelete(null);
      queryClient.invalidateQueries({ queryKey: ["admin-newsletter"] });
    } catch (e) {
      toast.error("Échec de la suppression : " + (e?.response?.data?.detail || e.message));
    } finally {
      setDeleting(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/newsletter/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `newsletter-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export CSV lancé (${subs.length} abonnés)`);
    } catch (e) {
      toast.error("Échec de l'export : " + (e?.response?.data?.detail || e.message));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <section className="border-b border-white/5">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-3">Administration</div>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-display font-black text-3xl sm:text-4xl">Newsletter</h1>
              <p className="mt-2 text-zinc-400 max-w-xl text-sm">
                Emails collectés via le formulaire de newsletter. Exportez la liste en CSV pour votre outil d'emailing.
              </p>
            </div>
            <Button
              onClick={exportCsv}
              disabled={exporting || subs.length === 0}
              className="bg-emerald-500 text-black hover:bg-emerald-300"
              data-testid="newsletter-export-btn"
            >
              <Download className="w-4 h-4 mr-1.5" /> {exporting ? "Export…" : "Exporter CSV"}
            </Button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-8 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="p-5 bg-card border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 grid place-items-center text-emerald-300">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Total abonnés</div>
                <div className="text-2xl font-display font-black" data-testid="newsletter-count">{subs.length}</div>
              </div>
            </div>
          </Card>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative max-w-md flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un email…"
              className="pl-9 bg-white/[0.03] border-white/10 h-11"
              data-testid="newsletter-search-input"
            />
          </div>
          {selected.size > 0 && (
            <Button
              onClick={() => setToDelete("__bulk__")}
              className="bg-red-500 text-white hover:bg-red-400"
              data-testid="newsletter-bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer la sélection ({selected.size})
            </Button>
          )}
        </div>

        <Card className="bg-card border-white/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="newsletter-table">
              <thead className="bg-white/[0.03] text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                <tr>
                  <th className="px-5 py-3 w-10">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      disabled={filtered.length === 0}
                      aria-label="Tout sélectionner"
                      data-testid="newsletter-select-all"
                    />
                  </th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-right px-5 py-3">Inscrit le</th>
                  <th className="text-right px-5 py-3 w-16">Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={4} className="px-5 py-10 text-center text-zinc-500">Chargement…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-10 text-center text-zinc-500">
                      {q ? `Aucun email ne correspond à « ${q} ».` : "Aucun abonné pour l'instant."}
                    </td>
                  </tr>
                ) : (
                  filtered.map((s) => (
                    <tr key={s.email} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`newsletter-row-${s.email}`}>
                      <td className="px-5 py-3">
                        <Checkbox
                          checked={selected.has(s.email)}
                          onCheckedChange={() => toggleOne(s.email)}
                          aria-label={`Sélectionner ${s.email}`}
                          data-testid={`newsletter-select-${s.email}`}
                        />
                      </td>
                      <td className="px-5 py-3 text-zinc-200 flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 text-zinc-500" /> {s.email}
                      </td>
                      <td className="px-5 py-3 text-right text-zinc-500 font-mono-num text-xs">
                        {s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setToDelete(s.email)}
                          className="inline-grid place-items-center w-8 h-8 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          aria-label={`Supprimer ${s.email}`}
                          data-testid={`newsletter-delete-${s.email}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="newsletter-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{isBulk ? `Supprimer ${bulkEmails.length} abonné(s) ?` : "Supprimer cet abonné ?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {isBulk ? (
                <>Les <span className="text-zinc-200 font-medium">{bulkEmails.length}</span> emails sélectionnés seront définitivement retirés de la liste newsletter. Cette action est irréversible.</>
              ) : (
                <>L'email <span className="text-zinc-200 font-medium">{toDelete}</span> sera définitivement retiré de la liste newsletter. Cette action est irréversible.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="newsletter-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-red-500 text-white hover:bg-red-400"
              data-testid="newsletter-delete-confirm"
            >
              {deleting ? "Suppression…" : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
